// Cellules d'index différées (cf. Shared::DeferredCellComponent) : au lieu d'une requête par
// cellule, les cellules d'une page qui visent le même endpoint sont regroupées en UNE requête
// portant leurs ids. Le serveur répond par des diffusions Turbo Stream, une par cellule, dès que
// chacune est prête ; ce module ne lit donc jamais le corps de la réponse.
//
// Deux pièges de ce schéma :
//
// 1. La diffusion n'est pas rejouée. Si elle part avant que le navigateur soit abonné au flux, la
//    cellule ne la reçoit jamais. On attend donc que l'abonnement soit confirmé (attribut
//    `connected` posé par <turbo-cable-stream-source>) AVANT d'envoyer la requête.
// 2. Une cellule que le serveur n'a pas servie (hors budget de temps, accès refusé, câble coupé)
//    resterait sur son indicateur de chargement pour toujours : passé un délai de grâce, elle
//    passe à l'état d'erreur compact déjà utilisé par les frames Turbo.
import { compactTurboFrameLoadErrorHtml, turboFrameLoadErrorMessage } from '../entrypoints/common';

type Pending = { url: string; stream: string; cells: Map<string, HTMLElement> };

const STREAM_CHANNEL = 'Turbo::StreamsChannel';

// Laisse les cellules d'un même rendu s'agréger avant d'envoyer.
const FLUSH_DELAY_MS = 50;
// Lots successifs, jamais en parallèle : un thread Puma à la fois par endpoint. Au-delà de
// `per` = 25 lignes visibles, le serveur plafonne de toute façon (DeferredCellsEndpoint::MAX_CELLS).
const CHUNK_SIZE = 25;
const STREAM_READY_TIMEOUT_MS = 5000;
// Les diffusions partent avant la fin de la réponse : ce délai couvre seulement leur acheminement.
const RESULT_GRACE_MS = 4000;

const pending = new Map<string, Pending>();
const sources = new Map<string, HTMLElement>();
let flushTimer: number | undefined;

export function requestDeferredCell(element: HTMLElement, { url, stream, id }: { url: string; stream: string; id: string }) {
  const key = `${url}\n${stream}`;
  const batch = pending.get(key) ?? { url, stream, cells: new Map() };
  batch.cells.set(id, element);
  pending.set(key, batch);

  window.clearTimeout(flushTimer);
  flushTimer = window.setTimeout(flush, FLUSH_DELAY_MS);
}

export function forgetDeferredCell(element: HTMLElement) {
  pending.forEach((batch) => {
    batch.cells.forEach((cell, id) => {
      if (cell === element) batch.cells.delete(id);
    });
  });
}

function flush() {
  const batches = [...pending.values()];
  pending.clear();
  batches.forEach((batch) => void send(batch));
}

async function send(batch: Pending) {
  const cells = liveCells(batch.cells);
  if (!cells.length) return;

  // Dans l'ordre de l'écran : le serveur répond dans l'ordre des ids, les valeurs apparaissent
  // donc de haut en bas. Les cellules paresseuses arrivent dans l'ordre où elles deviennent
  // visibles, qui n'est pas forcément celui du document.
  cells.sort(([, a], [, b]) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));

  if (!(await streamConnected(batch.stream))) {
    cells.forEach(([, element]) => failCell(element));
    return;
  }

  for (let i = 0; i < cells.length; i += CHUNK_SIZE) {
    // Une cellule peut avoir quitté la page pendant le lot précédent (pagination, recherche).
    const chunk = liveCells(new Map(cells.slice(i, i + CHUNK_SIZE)));
    if (!chunk.length) continue;

    try {
      await post(batch.url, chunk.map(([id]) => id));
    } catch {
      // Le délai de grâce ci-dessous marque ces cellules indisponibles.
    }
    window.setTimeout(() => chunk.forEach(([, element]) => failIfUnresolved(element)), RESULT_GRACE_MS);
  }
}

function liveCells(cells: Map<string, HTMLElement>): [string, HTMLElement][] {
  return [...cells].filter(([, element]) => element.isConnected);
}

async function post(url: string, ids: string[]) {
  const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';
  await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': token },
    body: JSON.stringify({ ids }),
  });
}

// Une diffusion qui a servi la cellule la REMPLACE : l'ancien élément quitte le DOM.
// Encore dans la page, c'est qu'elle n'a pas été servie.
function failIfUnresolved(element: HTMLElement) {
  if (element.isConnected) failCell(element);
}

function failCell(element: HTMLElement) {
  // Retire le contrôleur Stimulus pour qu'il ne redemande pas la cellule.
  element.removeAttribute('data-controller');
  element.innerHTML = compactTurboFrameLoadErrorHtml(turboFrameLoadErrorMessage());
}

// L'élément d'abonnement vit sous <html>, hors de <body> : Turbo remplace le body à chaque visite,
// ce qui désabonnerait le flux et obligerait à réattendre la confirmation à chaque page.
function streamConnected(signedStreamName: string): Promise<boolean> {
  let source = sources.get(signedStreamName);
  if (!source?.isConnected) {
    source = document.createElement('turbo-cable-stream-source');
    source.setAttribute('channel', STREAM_CHANNEL);
    source.setAttribute('signed-stream-name', signedStreamName);
    document.documentElement.appendChild(source);
    sources.set(signedStreamName, source);
  }
  const element = source;
  if (element.hasAttribute('connected')) return Promise.resolve(true);

  return new Promise((resolve) => {
    const finish = (connected: boolean) => {
      observer.disconnect();
      window.clearTimeout(timeout);
      resolve(connected);
    };
    const observer = new MutationObserver(() => {
      if (element.hasAttribute('connected')) finish(true);
    });
    const timeout = window.setTimeout(() => finish(false), STREAM_READY_TIMEOUT_MS);
    observer.observe(element, { attributes: true, attributeFilter: ['connected'] });
  });
}
